// NPM
import React, { useEffect, useState } from 'react'
import { useRecoilState } from "recoil";
import { isNil, forEach, isEmpty, map, filter, isArray, omit, groupBy } from 'lodash';
import { useHistory } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {Helmet} from "react-helmet";

// HELPERS
import { toaster, encode, decode, generateCode } from 'Helpers/index';

// COMPONENTS
import ProductCheckout from 'Components/product/product-checkout';

// RECOIL ATOM
import { orderAtom, profileAtom } from "Recoil/order.atom";
import { LocalCartAtom } from 'Recoil/order.atom';

// STYLE
import './product-checkout.scss';

// MODEL
import normalize from 'Utils/normalizer';

// SERVICE
import { fetchShippingInfo } from 'Services/shipping.service';
import { createOrder, addNewAddress, fetchProfileInfo, fetchCartList, updateCartItem } from 'Services/order.services';
import { fetchCheckoutVoucherList, verifyVoucherList } from 'Services/voucher.service';
import useQuery from 'Hooks/query.hook';
import { fetchQuotation } from 'Services/quotation.services';
import { Quotation } from 'Models/quotation';
import useSession from 'Hooks/session.hook';

interface ProductCheckoutPageProps { }

const ProductCheckoutPage: React.FC<ProductCheckoutPageProps> = () => {
  const history = useHistory();
  const [localCartState, setLocalCartState] = useRecoilState<any>(LocalCartAtom);
  const [orderState, setOrderState] = useRecoilState<any>(orderAtom);
  const [profileState, setProfileState] = useRecoilState<any>(profileAtom);
  const [profileInfo, setProfileInfo] = useState({})
  const [quotationData, setQuotationData] = useState<Quotation|undefined>();
  const { profile } = useSession();

  const { t } = useTranslation();
  const query = useQuery();
  const quotationID = query.get('quotationID') ? decode(query.get('quotationID') as string) : '';

  const handleSubmit = async (data: any) => {
    if (process.env.GATEWAY_TYPE === 'REVPAY') {
      history.push('/proceed');
      return;
    }

    setOrderState({ ...orderState, status: { ...orderState.status, create: 'loading' } });

    const responseData = await createOrder(data);
    // Testing for error toaster
    // const responseData = {
    //   success: false,
    //   data: null,
    //   error: {
    //     code: 6905,
    //     message: 'Could not verify voucher',
    //     type: 'CONTROLLER_ERROR',
    //     stack: { message: 'Voucher status is FULLY_REDEEMED' }
    //   }
    // }

    // if (!isNil(responseData) && !isNil(responseData.paymentID)) {
    if (!isNil(responseData) && !isNil(responseData.orders)) {
      const orderIDs = !isEmpty(responseData.orders) ? map(responseData.orders, (order) => order._id) : null;
      const encodedOrderIDs = encode(JSON.stringify(orderIDs));

      setOrderState({
        ...orderState,
        status: { ...orderState.status, create: 'success' },
      });

      if (!localCartState.fromBuyNow) {
        const savedCheckoutData = localCartState.list;

        let latestCartList: any;

        if (savedCheckoutData.length == carListItems.items.length) {
          latestCartList = [];
        } else {
          // map(savedCheckoutData, item => {
          //   latestCartList = filter(carListItems.items, obj => {
          //     return item.cartID != obj.cartID
          //   })
          // })
          latestCartList = carListItems.items.filter(obj =>
            savedCheckoutData.every(item => !item.cartID.includes(obj.cartID))
          );
        }

        let obj = {
          cardId: carListItems.cartID,
          items: latestCartList
        }
        const cartResponse = await updateCartItem(obj);
        if (cartResponse) {
          setLocalCartState({
            ...localCartState, list: [], appliedPlatformVouchers: data.paymentTotals?.appliedPlatformVouchers
          });

          history.push(`/payment/submit?ref=${encodedOrderIDs}`);
        }
      } else {
        setLocalCartState({
          ...localCartState, list: [], fromBuyNow: false, appliedPlatformVouchers: data.paymentTotals?.appliedPlatformVouchers
        });

        history.push(`/payment/submit?ref=${encodedOrderIDs}`);
      }
    }
    else {
      const {code,message} = responseData?.error;
      const stack = responseData?.error?.stack?.message;
      setOrderState({
        ...orderState,
        status: { ...orderState.status, create: 'error' }
      });
      if (stack){
        toaster('error', `${t('toaster.FailedToCreateOrder')}. \n ${message}. ${stack}`)
      } else{
        toaster('error', `${t('toaster.FailedToCreateOrder')}. \n ${message}.`)

      }

    }
  }

  const handleUpdateAddress = async (data) => {
    setProfileState({ ...profileState, status: { ...profileState.status, update: 'loading' } });

    const responseData = await addNewAddress(data);
    if (!isNil(responseData)) {
      setProfileState({
        ...profileState,
        status: { ...profileState.status, update: 'success' },
        data: responseData.profile
      });
      toaster('success', t('toaster.AddedNewAddressSuccessfully'));

    }
    else {
      setProfileState({
        ...profileState,
        status: { ...profileState.status, update: 'error' }
      });
      toaster('error', t('toaster.FailedtoAddNewaddress'))

    }
  }

  const getProfileInfo = async () => {
    setProfileState({ ...profileState, status: { ...profileState.status, fetch: 'loading' } });
    const responseData = await fetchProfileInfo();

    if (!isNil(responseData)) {
      setProfileState({
        ...profileState,
        status: { ...profileState.status, fetch: 'success' },
        data: responseData.profile
      });
      setProfileInfo(responseData)
    }
    else {
      setProfileState({
        ...profileState,
        status: { ...profileState.status, fetch: 'error' }
      });
    }
  }
  const [carListItems, setCarListItems] = useState<any>({});
  const fetchCartListItems = async () => {
    const responseData = await fetchCartList();
    console.debug('res', responseData);
    if (!isNil(responseData) && responseData.cartID) {
      setCarListItems(responseData)
    }
  }
  const fetchQuotationData = async () => {
    if (!quotationID) return;

    const response = await fetchQuotation({ _id: quotationID });
    if (!response || response.error) {
      return;
    }

    setQuotationData(response);

    const quotationDataResponse = response as Quotation;

    const itemsToAdd = quotationDataResponse.items;
    const cartList: any[] = [];
    let totalWeight = 0;

    for (const item of itemsToAdd) {
      const productData = item.itemData;

      // * refer product-details.tsx
      const productType = productData.product?.type;

      const foundVariant = item?.itemData?.product?.productVariants?.find(pv => pv.variantCode === item.variantCode);
      let itemWeight = foundVariant?.weight?.value ?? '0';
      const foundImageFileID = foundVariant?.images?.[0]?.fileID ?? productData?.product?.images?.[0]?.fileID;
      const foundImageUrl = !foundImageFileID ? '/assets/images/product-placeholder.png' :
        productData?.files_data?.find(f => f._id === foundImageFileID)?.file_url;
      const cartDetails = {
        cartID: generateCode('CRT-'),
        productData: {
          ...productData,
          inventoryID: productData._id,
          product: {
            ...productData.product,
            productType: productType,
            productName: productData.product?.name,
          },
          orgData: {
            ...quotationDataResponse.quotedByData,
            image_file: quotationDataResponse.quotedByData.file_data,
          },
        },
        selectedVariant: {
          value: item.unitPrice,
          variantCode: item.variantCode,
          variantName: item.variant,
          uom: productData?.product?.productVariants?.find(s => s.variantCode === item.variantCode)?.uom ?? '',
        },
        productType,
        quantity: item.quantity,
        variantImg: foundImageUrl,
        weight: productType != 'VOUCHER' && productType !== 'NO_SHIPPING' ? parseFloat(itemWeight) * parseFloat(item.quantity) : 0
      }

      if (productType !== 'VOUCHER' && productType !== 'NO_SHIPPING') {
        totalWeight += parseFloat(itemWeight) * parseFloat(item.quantity)
      }

      cartList.push(cartDetails)
    }

    const supplierGroup = groupBy(cartList, cartObj => {
      return cartObj.productData.orgID;
    })

    const shippingCostItems = map(supplierGroup, (data, id) => {
      let weight = 0;
      data.forEach((item) => {
        weight += item.weight // do not multiply by quantity, because it is already multiplied when creating cartDetails and putting into cartList
      })
      return {
        supplierID: id,
        state: profile.primaryAddress.state,
        weight: weight
      }
    })

    const shippingCostObj = {
      orders: shippingCostItems,
    }

    setLocalCartState({
      ...localCartState, list: [...cartList], fromBuyNow: true, shippingCostObj,
    });
  }

  const [costValues, setCostValues] = useState<any>([]);
  const getShippingCosts = async (obj) => {
    setCostValues([])
    const responseData = await fetchShippingInfo(obj);
    if (!isNil(responseData) && responseData.orders) {
      setCostValues(responseData.orders)
    }
  }

  const [checkoutPlatFormVoucherList, setCheckoutPlatformVoucherList] = useState<any>([]);
  const getCheckoutPlatformVoucherList = async (amount) => {
    // DEFAULT FILTER VALUE WITH DYNAMIC PURCHASE AMOUNT
    const input = {
      type: "PLATFORM",
      sourceSite: process.env.ACCOUNT_TYPE,
      platformFields: {
          purchaseAmount: amount
      }
    }
    const responseData = await fetchCheckoutVoucherList(input);
    // CHECK IF NOT NIL & OUTPUT IN ARRAY FORMAT
    if (!isNil(responseData) && isArray(responseData)) {
      setCheckoutPlatformVoucherList(responseData);
    }
  }

  const [checkoutItemVoucherList, setCheckoutItemVoucherList] = useState<any>([]);
  const getCheckoutItemVoucherList = async (input) => {
    const responseData = await fetchCheckoutVoucherList(input);
    if (!isNil(responseData) && isArray(responseData)) {
      setCheckoutItemVoucherList(responseData);
    }
  }

  // VERIFY VOUCHER FUNC
  const verifyVoucherFunc = async (input) => {

    // CALL API
    const responseData = await verifyVoucherList(input);

    // CHECK IF VALUE IS EMPTY
    if (!isNil(responseData)) {
      return responseData
    } else {
      // FALLBACK IF EMPTY, RETURN BACK CURRENT TOTAL AMOUNT
      return input['total']
    }
  }

  useEffect(() => {
    getProfileInfo()

    if (!quotationID) {
      fetchCartListItems()
      return;
    }
  }, [])

  useEffect(() => {
    console.debug('qu', quotationID);
    if (!quotationID) return;

    fetchQuotationData();
  }, [quotationID])

  const canShowCheckout = !!(quotationID && quotationData && localCartState.list.length) || !quotationID
  console.debug('can', canShowCheckout);

  return (
    <React.Fragment>
      <Helmet>
        <title>
          Checkout
        </title>
      </Helmet>
      <div className="page pageWrapper">
        <div className="page">
          {
            canShowCheckout &&
            <ProductCheckout
              profileInfoObj={profileInfo}
              status={orderState.status}
              profileStatus={profileState.status}
              handleSubmit={handleSubmit}
              handleUpdate={handleUpdateAddress}
              fetchProfileData={getProfileInfo}
              fetchShippingCosts={getShippingCosts}
              fetchPlatformVoucherList={getCheckoutPlatformVoucherList}
              fetchItemVoucherList={getCheckoutItemVoucherList}
              checkoutPlatFormVoucherList={checkoutPlatFormVoucherList}
              checkoutItemVoucherList={checkoutItemVoucherList}
              verifyVoucher={verifyVoucherFunc}
              shippingCosts={costValues}
              quotationID={quotationID}
            />
          }
        </div>
      </div>
    </React.Fragment>
  )
}

export default ProductCheckoutPage;
